/**
 * App Store purchases in the iPad app (ipad-native-shell 10.3).
 *
 * StoreKit buys; the server decides. Every purchase carries the family's tenant
 * id as `appAccountToken` (the 10.4 server contract: without it every renewal
 * and notification is ignored), and its signed transaction goes to
 * `POST /billing/apple/transactions`, whose answer is the family's new billing
 * state. A transaction is finished only after the server has accepted it, so
 * one the server never saw comes back at the next launch.
 *
 * Dark until configured: `loadAppStoreOffers()` returns [] unless the server
 * lists products (`APPLE_IAP_*` set) AND the native plugin is present AND Apple
 * knows the ids (10.2), and the paywall keeps its no-purchase copy until then.
 * The caller puts the Parental_Gate in front of `buyOnAppStore`.
 */
import { getAppleProducts, postAppleTransaction } from "./api";
import { publishBillingState } from "./useBilling";
import { requestMailingPrompt } from "./mailingAddress";
import { isGrownUpRole } from "./grownUpRole";
import type { BillingState, PlanId } from "./types";
import {
  isStoreKitAvailable,
  storeKit,
  type StoreKit,
  type StoreKitProduct,
  type StoreKitTransaction,
} from "../platform/storeKit";

export interface AppStoreOffer extends StoreKitProduct {
  planId: PlanId;
}

export type PurchaseOutcome =
  | { kind: "unlocked"; state: BillingState }
  | { kind: "cancelled" }
  | { kind: "pending" }
  /** Apple took the purchase but the server has not confirmed it yet. */
  | { kind: "unconfirmed" }
  | { kind: "failed" };

interface Deps {
  available: () => boolean;
  kit: StoreKit;
  products: typeof getAppleProducts;
  post: typeof postAppleTransaction;
  publish: (s: BillingState) => void;
}

const live: Deps = {
  available: isStoreKitAvailable,
  kit: storeKit,
  products: getAppleProducts,
  post: postAppleTransaction,
  publish: publishBillingState,
};
let deps: Deps = live;

/** Tests only. */
export function _setAppStoreDepsForTesting(d: Partial<Deps> | null): void {
  deps = d ? { ...live, ...d } : live;
}

/**
 * Whether this role may buy or restore. The server takes a transaction only
 * from a parent or an adult (`require_parent_account`); a teen or a kid who
 * paid Apple would get a 403, the purchase would never finish and nothing
 * would unlock, so they never see the button.
 */
export function canBuyOnAppStore(role: string | undefined): boolean {
  return isGrownUpRole(role);
}

export function appStoreAvailable(): boolean {
  return deps.available();
}

/** "$9.99 / month", or "Free for 7 days, then $9.99 / month" when this Apple ID may take the trial. */
function per(o: AppStoreOffer): string {
  if (!o.periodUnit) return "";
  return o.periodValue && o.periodValue > 1 ? ` / ${o.periodValue} ${o.periodUnit}s` : ` / ${o.periodUnit}`;
}

export function priceLine(o: AppStoreOffer): string {
  const price = `${o.displayPrice}${per(o)}`;
  return o.freeTrialDays && o.eligibleForIntroOffer
    ? `Free for ${o.freeTrialDays} days, then ${price}`
    : price;
}

export async function loadAppStoreOffers(): Promise<AppStoreOffer[]> {
  if (!deps.available()) return [];
  try {
    const cfg = await deps.products();
    if (!cfg.enabled || cfg.products.length === 0) return [];
    const plans = new Map(cfg.products.map((p) => [p.product_id, p.plan_id as PlanId]));
    const found = await deps.kit.products([...plans.keys()]);
    return found
      .filter((p) => plans.has(p.id))
      .map((p) => ({ ...p, planId: plans.get(p.id) as PlanId }));
  } catch (err) {
    console.warn("[appStore] offers unavailable:", err);
    return [];
  }
}

/** Server first, then finish. Returns the new state, or null if not accepted. */
async function deliver(t: StoreKitTransaction): Promise<BillingState | null> {
  let state: BillingState;
  try {
    state = await deps.post(t.jws);
  } catch (err) {
    console.warn("[appStore] server did not accept the transaction:", err);
    return null;
  }
  deps.publish(state);
  try {
    await deps.kit.finish(t.transactionId);
  } catch (err) {
    // Not fatal: an unfinished transaction is replayed and re-posted, and the
    // server's handling is idempotent.
    console.warn("[appStore] finish failed:", err);
  }
  return state;
}

export async function buyOnAppStore(productId: string, tenantId: string): Promise<PurchaseOutcome> {
  let result;
  try {
    result = await deps.kit.purchase(productId, tenantId);
  } catch (err) {
    console.warn("[appStore] purchase failed:", err);
    return { kind: "failed" };
  }
  if (result.status === "cancelled") return { kind: "cancelled" };
  if (result.status === "pending") return { kind: "pending" };
  const state = await deliver(result);
  if (!state) return { kind: "unconfirmed" };
  // A purchase the grown-up made just now, accepted by the server: ask once for
  // a mailing address (train 17). Never on renewals, replays or restore.
  requestMailingPrompt();
  return { kind: "unlocked", state };
}

export interface RestoreOutcome {
  /** How many entitlements the server took. */
  restored: number;
  /** The billing state the server returned for the last one it took, or null. */
  state: BillingState | null;
}

/** "Restore purchases": re-post every current entitlement. */
export async function restoreAppStorePurchases(): Promise<RestoreOutcome> {
  let txs: StoreKitTransaction[];
  try {
    txs = await deps.kit.currentEntitlements();
  } catch (err) {
    console.warn("[appStore] restore failed:", err);
    return { restored: 0, state: null };
  }
  let restored = 0;
  let state: BillingState | null = null;
  for (const t of txs) {
    const s = await deliver(t);
    if (s) {
      restored += 1;
      state = s;
    }
  }
  return { restored, state };
}

export async function manageOnAppStore(): Promise<boolean> {
  if (!deps.available()) return false;
  try {
    await deps.kit.manageSubscriptions();
    return true;
  } catch {
    return false;
  }
}

/**
 * Renewals, Ask to Buy approvals and the unfinished replay at launch arrive as
 * events. Deliver each one; the server checks it is this family's. Returns an
 * unsubscribe.
 */
export function listenForAppStoreTransactions(): () => void {
  if (!deps.available()) return () => {};
  let handle: { remove: () => Promise<void> } | null = null;
  let stopped = false;
  void deps.kit
    .onTransactionUpdated((t) => {
      void deliver(t);
    })
    .then((h) => {
      if (stopped) void h.remove();
      else handle = h;
    })
    .catch((err) => console.warn("[appStore] listener unavailable:", err));
  return () => {
    stopped = true;
    void handle?.remove();
  };
}
